/**
 * theme-presets (2026-08-20) — "테마 만들기" 의 **불러오기**와 **등록**.
 *
 * 두 종류가 있다.
 *   ① 기본 제공 테마 — 앱이 들고 있는 색 묶음. 출발점으로만 쓴다(고쳐도 원본은 안 바뀐다).
 *   ② 내 테마       — 지금 화면의 색·모서리·속도를 **이름 붙여 등록**한 것. 계정에 저장한다.
 *
 * ★ 기본 제공 테마는 **주색·보조색·위험색만** 담는다. 라인색·표면색은 라이트/다크마다 값이
 *   달라야 하는 색이라, 밝은 표면색을 통째로 얹으면 다크 모드에서 글자가 안 읽힌다.
 *   비워 두면 tokens.css 가 모드에 맞는 값을 그대로 쓴다(= 지금과 같다).
 *
 * ★ 내 테마는 반대로 **화면에 있던 것을 그대로** 담는다(라인·표면·모서리·속도 포함).
 *   사용자가 만든 것을 되살리는 자리이므로 일부만 살리면 "그때 그 화면" 이 되지 않는다.
 *
 * 저장 자리: 서버 `user_settings.themePresets`(migration 108). 구 서버라 그 칸이 없으면
 * localStorage 로 떨어진다 — 기능이 죽지 않게 하려는 것이고, 그때는 화면이 "이 기기에만
 * 저장됨" 이라고 알린다.
 */

import { isHex, type ButtonRadius } from './theme-custom';
import type { SettingsI18nKey } from './settings-i18n';
import { THEME_CATALOG } from './theme-catalog';

/** 등록한 테마 하나. 서버 JSON 과 같은 모양이다(스키마는 user-settings.routes.ts). */
export interface SavedTheme {
  id: string;
  name: string;
  primary: string | null;
  accent: string | null;
  danger: string | null;
  line: string | null;
  surface: string | null;
  /* 영역별 색 (2026-08-20) — 등록 당시 화면을 그대로 되살리려면 이것도 함께 담아야 한다. */
  bg: string | null;
  rail: string | null;
  menu: string | null;
  rrail: string | null;
  status: string | null;
  table: string | null;
  tableHead: string | null;
  tableHeadText: string | null;
  icon: string | null;
  iconHover: string | null;
  tint: string | null;
  text: string | null;
  textMuted: string | null;
  buttonRadius: ButtonRadius;
  transitionMs: number;
  /** ISO. 목록 정렬·표시에만 쓴다. */
  createdAt: string;
}

/** 영역별 색 키 — 다듬기·복사에서 한 곳만 고치면 되게 모아 둔다. */
export const AREA_KEYS = ['bg', 'rail', 'menu', 'rrail', 'status', 'table', 'tableHead',
  'tableHeadText', 'icon', 'iconHover', 'tint', 'text', 'textMuted'] as const;

/** 앱이 들고 있는 색 묶음 — 고쳐 쓰라고 있는 출발점이다. */
export interface BuiltInTheme {
  id: string;
  nameKey: SettingsI18nKey;
  primary: string;
  accent: string;
  danger: string;
}

/** 이름 길이·개수 상한 — 서버 스키마(user-settings.routes.ts)와 같은 값이어야 한다. */
export const THEME_NAME_MAX = 20;
export const MAX_SAVED_THEMES = 20;

/**
 * 불러오기 목록 = **테마 설정 화면과 같은 12종**([[theme-catalog]]). 이름이 두 화면에서
 * 갈리면 "아까 그 테마" 를 못 찾는다. 여기서는 색만 쓴다 — 모드는 테마 설정이 다룬다.
 */
export const BUILT_IN_THEMES: ReadonlyArray<BuiltInTheme> = THEME_CATALOG.map((t) => ({
  id: t.id,
  nameKey: t.nameKey,
  primary: t.primary,
  accent: t.accent,
  danger: t.danger,
}));

function isRadius(v: unknown): v is ButtonRadius {
  return v === 'square' || v === 'round' || v === 'pill';
}

function hexOrNull(v: unknown): string | null {
  return isHex(v) ? v : null;
}

/**
 * 서버·localStorage 에서 읽은 값을 믿지 않고 다듬는다.
 * 색 하나가 이상해도 목록 전체가 사라지지 않게, **항목 단위**로 걸러 낸다.
 */
export function sanitizeSavedThemes(raw: unknown): SavedTheme[] {
  if (!Array.isArray(raw)) return [];
  const out: SavedTheme[] = [];
  for (const item of raw) {
    if (!item || typeof item !== 'object') continue;
    const t = item as Record<string, unknown>;
    const id = typeof t.id === 'string' && t.id.length > 0 ? t.id : null;
    const name = typeof t.name === 'string' && t.name.trim().length > 0 ? t.name.trim().slice(0, THEME_NAME_MAX) : null;
    if (!id || !name) continue;
    const areas = Object.fromEntries(AREA_KEYS.map((k) => [k, hexOrNull(t[k])])) as
      Pick<SavedTheme, (typeof AREA_KEYS)[number]>;
    out.push({
      id,
      name,
      primary: hexOrNull(t.primary),
      accent: hexOrNull(t.accent),
      danger: hexOrNull(t.danger),
      line: hexOrNull(t.line),
      surface: hexOrNull(t.surface),
      ...areas,
      buttonRadius: isRadius(t.buttonRadius) ? t.buttonRadius : 'round',
      transitionMs: typeof t.transitionMs === 'number' && t.transitionMs >= 0 && t.transitionMs <= 2000
        ? Math.round(t.transitionMs)
        : 350,
      createdAt: typeof t.createdAt === 'string' ? t.createdAt : '',
    });
    if (out.length >= MAX_SAVED_THEMES) break;
  }
  return out;
}

/** 충돌 없는 id — 같은 계정이 두 기기에서 등록해도 겹치지 않게 랜덤을 쓴다. */
function newId(): string {
  const c = typeof crypto !== 'undefined' ? crypto : undefined;
  if (c && typeof c.randomUUID === 'function') return c.randomUUID();
  return `t${Math.random().toString(36).slice(2, 10)}${Math.random().toString(36).slice(2, 6)}`;
}

/** 지금 화면 값 + 이름 → 등록할 테마 하나. */
export function makeSavedTheme(
  name: string,
  from: Omit<SavedTheme, 'id' | 'name' | 'createdAt'>,
  nowIso: string,
): SavedTheme {
  return {
    ...from,
    id: newId(),
    name: name.trim().slice(0, THEME_NAME_MAX),
    createdAt: nowIso,
  };
}

/** 이름이 겹치면 뒤에 번호를 붙인다 — 목록에서 어느 것인지 구분이 안 되면 불러오기가 도박이 된다. */
export function uniqueName(base: string, existing: ReadonlyArray<SavedTheme>): string {
  const taken = new Set(existing.map((t) => t.name));
  const trimmed = base.trim().slice(0, THEME_NAME_MAX) || 'My theme';
  if (!taken.has(trimmed)) return trimmed;
  for (let i = 2; i < 100; i += 1) {
    const suffix = ` ${i}`;
    const candidate = `${trimmed.slice(0, THEME_NAME_MAX - suffix.length)}${suffix}`;
    if (!taken.has(candidate)) return candidate;
  }
  return trimmed;
}

/* ── 기기 저장(폴백) ─────────────────────────────────────────────────────────
   서버에 칸이 없을 때만 쓴다. 계정 저장이 되는 순간 이 값은 서버로 한 번 올라가고,
   그 뒤로는 서버가 SSOT 다. */

const LOCAL_KEY = 'wc.theme.presets.v1';

export function readLocalThemes(): SavedTheme[] {
  try {
    const raw = localStorage.getItem(LOCAL_KEY);
    return raw ? sanitizeSavedThemes(JSON.parse(raw)) : [];
  } catch {
    return [];
  }
}

export function writeLocalThemes(list: ReadonlyArray<SavedTheme>): void {
  try {
    localStorage.setItem(LOCAL_KEY, JSON.stringify(list));
  } catch {
    /* 저장 공간이 없으면 조용히 넘어간다 — 화면은 이미 목록을 들고 있다. */
  }
}
